// src/pages/URDReport.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFilter } from "react-icons/fa";
import {
  FiBarChart2,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiDownload,
  FiSearch,
  FiSettings,
  FiX,
} from "react-icons/fi";
import Select from "react-select";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";

import { useDispatch, useSelector } from "react-redux";
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setBranchList } from "../slices/branchSlice";
import { handleDropDowns } from "../config/dropDownHelpers";

// 🔗 URD report hook
import { useGetUrdReportQuery } from "../toolkitServices/urdApi";

/* ---------- helpers ---------- */
const toNum = (v) => Number(v ?? 0) || 0;
const fmt0 = (n) => (Number.isFinite(n) ? Number(n).toFixed(0) : "0");
const fmt2 = (n) => (Number.isFinite(n) ? Number(n).toFixed(2) : "0.00");
const fmt3 = (n) => (Number.isFinite(n) ? Number(n).toFixed(3) : "0.000");
const nonEmpty = (s) => typeof s === "string" && s.trim().length > 0;

/* ------------ tiny spinner ------------ */
const Spinner = ({ size = 14, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

// react-select styles (more premium)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 12,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px #7c3aed22" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.85rem",
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// skeleton row
const SkeletonRow = ({ cols = 10 }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-3 py-2">
        <div className="h-4 w-full bg-gray-200/70 rounded animate-pulse" />
      </td>
    ))}
  </tr>
);

export default function URDReport() {
  const dispatch = useDispatch();

  /* ---------- local UI state ---------- */
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  const [search, setSearch] = useState("");

  // server-side pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // date range
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");

  // column toggles (defaults aligned for URD KPIs)
  const [cols, setCols] = useState({
    items: true,
    qty: true,
    total: false,
    packing: false,
    gross: false,
    stone: false,
    diamond: false,
    net: true,
    fine: true,
    amount: true,
  });
  const [dense, setDense] = useState(true);

  /* ---------- fetch masters ---------- */
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();

  // push masters to redux store
  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories?.data));
    if (products?.data?.length) dispatch(setProductList(products?.data));
    if (branches?.data?.length) dispatch(setBranchList(branches?.data));
  }, [dispatch, categories, products, branches]);

  // store → lists
  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  /* ---------- filters (applied vs draft) ---------- */
  const [appliedFilters, setAppliedFilters] = useState({
    branchId: "",
    categoryId: "",
    productId: "",
  });
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  // options via helper
  const findOpt = (opts, v) =>
    (opts || []).find((o) => String(o.value) === String(v)) || null;

  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState }),
    [branchesFromState],
  );
  const categoryOptions = useMemo(
    () => handleDropDowns({ key: "category", categories: categoriesFromState }),
    [categoriesFromState],
  );

  // DRAFT selected objects
  const branchDraft = useMemo(
    () => findOpt(branchOptions, draftFilters.branchId),
    [branchOptions, draftFilters.branchId],
  );
  const categoryDraft = useMemo(
    () => findOpt(categoryOptions, draftFilters.categoryId),
    [categoryOptions, draftFilters.categoryId],
  );

  const productOptions = useMemo(
    () =>
      handleDropDowns({
        key: "product",
        products: productsFromState,
        category: categoryDraft,
      }),
    [productsFromState, categoryDraft],
  );
  const productDraft = useMemo(
    () => findOpt(productOptions, draftFilters.productId),
    [productOptions, draftFilters.productId],
  );

  // Applied objects (for chips)
  const branchApplied = useMemo(
    () => findOpt(branchOptions, appliedFilters.branchId),
    [branchOptions, appliedFilters.branchId],
  );
  const categoryApplied = useMemo(
    () => findOpt(categoryOptions, appliedFilters.categoryId),
    [categoryOptions, appliedFilters.categoryId],
  );
  const productApplied = useMemo(
    () =>
      findOpt(
        handleDropDowns({
          key: "product",
          products: productsFromState,
          category: categoryApplied,
        }),
        appliedFilters.productId,
      ),
    [appliedFilters.productId, productsFromState, categoryApplied],
  );

  /* ---------- API call: URD Report (server-side pagination) ---------- */
  const queryArgs = useMemo(() => {
    const params = {
      page,
      limit,
      branchId: appliedFilters.branchId || undefined,
      categoryId: appliedFilters.categoryId || undefined,
      productId: appliedFilters.productId || undefined,
    };
    if (nonEmpty(from)) params.from = from;
    if (nonEmpty(to)) params.to = to;
    return params;
  }, [appliedFilters, from, to, page, limit]);

  const { data, isFetching, isError, error } = useGetUrdReportQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const rows = data?.rows || [];
  const grand = data?.grandTotal || {};
  const total = data?.total ?? 0;
  const totalPages = data?.pages ?? Math.max(1, Math.ceil(total / limit));

  const humanRange = useMemo(() => {
    if (!data?.range?.start && !data?.range?.end) return "";
    const s = data?.range?.start ? new Date(data.range.start) : null;
    const e = data?.range?.end ? new Date(data.range.end) : null;
    const opt = { day: "2-digit", month: "short", year: "numeric" };
    if (s && e)
      return `${s.toLocaleDateString("en-IN", opt)} — ${e.toLocaleDateString(
        "en-IN",
        opt,
      )}`;
    if (s) return `${s.toLocaleDateString("en-IN", opt)}`;
    if (e) return `${e.toLocaleDateString("en-IN", opt)}`;
    return "";
  }, [data]);

  /* ---------- client-side search (current page only) ---------- */
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r?.branch?.name || "",
        r?.category?.name || "",
        r?.product?.name || "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  /* ---------- CSV export (current page; respects column toggles) ---------- */
  const exportCSV = () => {
    const head = [
      "Branch",
      "Category",
      "Product",
      ...(cols.items ? ["Items"] : []),
      ...(cols.qty ? ["Qty"] : []),
      ...(cols.total ? ["Total Wt"] : []),
      ...(cols.packing ? ["Packing Wt"] : []),
      ...(cols.gross ? ["Gross Wt"] : []),
      ...(cols.stone ? ["Stone Wt"] : []),
      ...(cols.diamond ? ["Diamond Wt (ct)"] : []),
      ...(cols.net ? ["Net Wt"] : []),
      ...(cols.fine ? ["Fine Wt"] : []),
      ...(cols.amount ? ["Amount (₹)"] : []),
    ];
    const lines = [head.join(",")];

    visibleRows.forEach((r) => {
      const cell = [
        (r.branch?.name ?? "").replaceAll(",", " "),
        (r.category?.name ?? "").replaceAll(",", " "),
        (r.product?.name ?? "").replaceAll(",", " "),
      ];
      if (cols.items) cell.push(fmt0(toNum(r.items)));
      if (cols.qty) cell.push(fmt0(toNum(r.qty)));
      if (cols.total) cell.push(fmt3(toNum(r.totalWt)));
      if (cols.packing) cell.push(fmt3(toNum(r.packingWt)));
      if (cols.gross) cell.push(fmt3(toNum(r.grossWt)));
      if (cols.stone) cell.push(fmt3(toNum(r.stoneWt)));
      if (cols.diamond) cell.push(fmt3(toNum(r.diamondWt)));
      if (cols.net) cell.push(fmt3(toNum(r.netWt)));
      if (cols.fine) cell.push(fmt3(toNum(r.fineWt)));
      if (cols.amount) cell.push(fmt2(toNum(r.amount)));
      lines.push(cell.join(","));
    });

    const g = grand || {};
    const footer = [
      "GRAND",
      "",
      "",
      ...(cols.items ? [fmt0(toNum(g.items))] : []),
      ...(cols.qty ? [fmt0(toNum(g.qty))] : []),
      ...(cols.total ? [fmt3(toNum(g.totalWt))] : []),
      ...(cols.packing ? [fmt3(toNum(g.packingWt))] : []),
      ...(cols.gross ? [fmt3(toNum(g.grossWt))] : []),
      ...(cols.stone ? [fmt3(toNum(g.stoneWt))] : []),
      ...(cols.diamond ? [fmt3(toNum(g.diamondWt))] : []),
      ...(cols.net ? [fmt3(toNum(g.netWt))] : []),
      ...(cols.fine ? [fmt3(toNum(g.fineWt))] : []),
      ...(cols.amount ? [fmt2(toNum(g.amount))] : []),
    ];
    lines.push(footer.join(","));

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = (from ? from : "start") + "_to_" + (to ? to : "end");
    a.download = `urd-report_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ---------- modal helpers ---------- */
  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setDraftFrom(from);
    setDraftTo(to);
    setIsFilterOpen(true);
  };
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setFrom(draftFrom);
    setTo(draftTo);
    setPage(1);
    setIsFilterOpen(false);
  };

  // react-select onChange handlers (DRAFT)
  const onBranchDraft = (opt) =>
    setDraftFilters((s) => ({ ...s, branchId: opt?.value || "" }));
  const onCategoryDraft = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      categoryId: opt?.value || "",
      productId: "",
    }));
  const onProductDraft = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      productId: opt?.value || "",
    }));

  // chips: clear single filter
  const clearOne = (key) => {
    setAppliedFilters((prev) => ({ ...prev, [key]: "" }));
    setPage(1);
  };

  // dynamic col count
  const metricColsCount =
    (cols.items ? 1 : 0) +
    (cols.qty ? 1 : 0) +
    (cols.total ? 1 : 0) +
    (cols.packing ? 1 : 0) +
    (cols.gross ? 1 : 0) +
    (cols.stone ? 1 : 0) +
    (cols.diamond ? 1 : 0) +
    (cols.net ? 1 : 0) +
    (cols.fine ? 1 : 0) +
    (cols.amount ? 1 : 0);

  const totalCols = 3 + metricColsCount;

  const canPrev = page > 1;
  const canNext = page < totalPages;

  /* ---------- close modals on ESC ---------- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsFilterOpen(false);
        setIsSummaryOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ---------- UI ---------- */
  return (
    <div className="relative bg-white shadow-lg rounded-xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)] overflow-hidden border">
      {/* Header */}
      <div className="hidden">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_400px_at_0%_0%,#b9abff33,transparent),radial-gradient(800px_300px_at_100%_0%,#c4bbff33,transparent)]" />
        <div className="absolute inset-0 bg-[#9481ce] " />
        <div className="relative flex flex-col gap-2 px-3 py-2.5 text-white sm:px-4 sm:py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold tracking-tight drop-shadow-sm sm:text-xl">
                URD Report
              </h2>
              <p className="hidden text-[11px] text-white/85 sm:block sm:text-xs">
                Branch → Category → Product • Qty/Weights/Fine/Amount
              </p>
              {humanRange && (
                <div className="mt-0.5 truncate text-[9px] text-white/90 sm:text-[11px]">
                  Range: <span className="font-semibold">{humanRange}</span>
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              {isFetching && (
                <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
                  <Spinner size={10} /> refreshing…
                </span>
              )}

              <button
                type="button"
                onClick={() => setIsSummaryOpen(true)}
                disabled={!rows.length}
                className="inline-flex h-8 w-8 items-center justify-center gap-1.5 rounded-xl bg-white text-[#6b59c6] shadow-sm transition hover:bg-violet-50 disabled:opacity-60 sm:w-auto sm:rounded-full sm:px-3"
                title="Summary"
              >
                <FiBarChart2 />
                <span className="hidden text-[11px] sm:inline">Summary</span>
              </button>

              <button
                onClick={openFilters}
                className="inline-flex h-8 w-8 items-center justify-center gap-1.5 rounded-xl bg-white/15 shadow-sm transition hover:bg-white/20 sm:w-auto sm:rounded-full sm:px-3"
                title="Filters"
              >
                <FaFilter />
                <span className="hidden text-[11px] sm:inline">Filters</span>
              </button>

              <button
                onClick={exportCSV}
                disabled={!visibleRows.length}
                className="inline-flex h-8 w-8 items-center justify-center gap-1.5 rounded-xl bg-emerald-500 shadow-sm transition hover:bg-emerald-600 disabled:opacity-60 sm:w-auto sm:rounded-full sm:px-3"
                title="Export CSV"
              >
                <FiDownload />
                <span className="hidden text-[11px] sm:inline">Export CSV</span>
              </button>
            </div>
          </div>

          {/* Search + Dates + Columns row */}
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex h-9 items-center gap-2 rounded-xl border border-white/10 bg-white/15 px-3 shadow-inner">
                <FiSearch className="shrink-0 text-white/70" />
                <input
                  className="min-w-0 flex-1 bg-transparent text-[11px] placeholder-white/70 focus:outline-none sm:text-sm"
                  placeholder="Search (branch / category / product)…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="hidden">
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
                className="px-2 py-2 rounded-xl bg-white/90 text-[#4b3d91] shadow-inner text-[12px]"
              />
              <span className="text-white/80 text-xs">to</span>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
                className="px-2 py-2 rounded-xl bg-white/90 text-[#4b3d91] shadow-inner text-[12px]"
              />
            </div>

            <ColumnPicker
              cols={cols}
              setCols={setCols}
              dense={dense}
              setDense={setDense}
            />
          </div>
        </div>
      </div>

      {/* Report header */}
      <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FiBarChart2 className="text-base" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              URD Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:hidden">
              {humanRange || "URD inventory summary"}
            </p>
          </div>
          {isFetching && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 text-[10px] text-violet-100 sm:text-[11px]">
          <span className="rounded-full bg-white/15 px-2.5 py-1 font-medium ring-1 ring-white/10">
            {total} <span className="hidden sm:inline">records</span>
          </span>
          {humanRange && (
            <span className="hidden rounded-full bg-white/10 px-2 py-1 lg:inline-flex">
              {humanRange}
            </span>
          )}
        </div>
      </div>

      {/* Search and actions */}
      <div className="border-b bg-slate-50/80 px-2 py-2 sm:px-4">
        <div className="flex items-center gap-2">
          <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-sm focus-within:border-violet-300 focus-within:ring-1 focus-within:ring-violet-200 lg:h-8 lg:rounded-lg lg:shadow-none">
            <FiSearch className="shrink-0 text-slate-400" />
            <input
              type="text"
              placeholder="Search branch, category or product…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="min-w-0 flex-1 bg-transparent text-[11px] text-slate-800 outline-none"
            />
          </div>

          <button
            type="button"
            onClick={openFilters}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-1 rounded-xl border border-violet-200 bg-white text-[#6b59c6] shadow-sm lg:h-8 lg:w-auto lg:rounded-full lg:px-3 lg:shadow-none"
            title="Filters"
            aria-label="Advanced filters"
          >
            <FaFilter />
            <span className="hidden text-[11px] lg:inline">Filters</span>
          </button>

          <button
            type="button"
            onClick={exportCSV}
            disabled={!visibleRows.length}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-1 rounded-xl bg-emerald-600 text-white shadow-sm disabled:opacity-50 lg:h-8 lg:w-auto lg:rounded-full lg:px-3"
            title="Export CSV"
            aria-label="Export CSV"
          >
            <FiDownload />
            <span className="hidden text-[11px] lg:inline">Export</span>
          </button>
        </div>

        {isFilterOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-[1px] lg:hidden"
              onClick={() => setIsFilterOpen(false)}
              aria-label="Close filters"
            />
            <div className="fixed inset-x-3 bottom-3 z-50 max-h-[calc(100vh-100px)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl lg:static lg:mt-3 lg:max-h-none lg:overflow-visible lg:p-3 lg:shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-slate-800">
                    Advanced Filters
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-500">
                    Choose the report period, inventory filters and columns.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 lg:hidden"
                  aria-label="Close filters"
                >
                  <FiX />
                </button>
              </div>

              <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                <div className="mb-2 text-[11px] font-semibold text-slate-700">
                  Report period
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="min-w-0 text-[10px] text-slate-500">
                    From
                    <input
                      type="date"
                      value={draftFrom}
                      onChange={(event) => setDraftFrom(event.target.value)}
                      className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none"
                    />
                  </label>
                  <label className="min-w-0 text-[10px] text-slate-500">
                    To
                    <input
                      type="date"
                      value={draftTo}
                      onChange={(event) => setDraftTo(event.target.value)}
                      className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none"
                    />
                  </label>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-[11px] text-slate-600">
                    Branch
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={branchOptions}
                    value={branchDraft}
                    onChange={onBranchDraft}
                    placeholder="Select branch"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-slate-600">
                    Category
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={categoryOptions}
                    value={categoryDraft}
                    onChange={onCategoryDraft}
                    placeholder="Select category"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-slate-600">
                    Product
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={productOptions}
                    value={productDraft}
                    onChange={onProductDraft}
                    placeholder="Select product"
                    isDisabled={!draftFilters.categoryId}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-slate-200 p-3">
                <div className="mb-2 text-[11px] font-semibold text-slate-700">
                  Columns
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                  {[
                    ["items", "Items"],
                    ["qty", "Qty"],
                    ["total", "Total Wt"],
                    ["packing", "Packing Wt"],
                    ["gross", "Gross Wt"],
                    ["stone", "Stone Wt"],
                    ["diamond", "Diamond"],
                    ["net", "Net Wt"],
                    ["fine", "Fine Wt"],
                    ["amount", "Amount"],
                  ].map(([key, label]) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-[11px] text-slate-600"
                    >
                      <input
                        type="checkbox"
                        checked={!!cols[key]}
                        onChange={(event) =>
                          setCols((current) => ({
                            ...current,
                            [key]: event.target.checked,
                          }))
                        }
                        className="h-4 w-4 accent-violet-600"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="rounded-full border border-slate-200 px-4 py-2 text-[11px] text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyFilters}
                  className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white"
                >
                  Apply & Run
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Applied filter chips */}
      <AnimatePresence initial={false}>
        {(appliedFilters.branchId ||
          appliedFilters.categoryId ||
          appliedFilters.productId) && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="px-3 sm:px-4 py-2 bg-slate-50 border-b flex flex-wrap gap-2 items-center"
          >
            <span className="text-[11px] text-slate-500">Applied:</span>

            {branchApplied && (
              <Chip
                label={`Branch: ${branchApplied.label}`}
                onClear={() => clearOne("branchId")}
              />
            )}
            {categoryApplied && (
              <Chip
                label={`Category: ${categoryApplied.label}`}
                onClear={() => clearOne("categoryId")}
              />
            )}
            {productApplied && (
              <Chip
                label={`Product: ${productApplied.label}`}
                onClear={() => clearOne("productId")}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls bar (like sales list footer header) */}
      <div className="hidden">
        <div className="flex items-center gap-2 text-[12px] text-slate-600">
          <span>
            Total: <b className="text-slate-900">{total}</b>
          </span>
          <span className="opacity-40">•</span>
          <span>
            Page <b className="text-slate-900">{page}</b> / {totalPages}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[12px] text-slate-600">Rows</span>
          <select
            className="px-2 py-1.5 rounded-full border text-[12px] bg-white focus:outline-none focus:ring-1 focus:ring-violet-400"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>

          <button
            className="px-3 py-1.5 rounded-full border text-[12px] hover:bg-white disabled:opacity-50"
            disabled={!canPrev || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <button
            className="px-3 py-1.5 rounded-full border text-[12px] hover:bg-white disabled:opacity-50"
            disabled={!canNext || isFetching}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
        <table
          className={`min-w-[980px] w-full text-sm ${
            dense ? "leading-tight" : "leading-normal"
          }`}
        >
          <thead className="bg-[#efeafd] text-[#4b3d91] uppercase text-[10px] sm:text-[11px] sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="w-[140px] min-w-[140px] bg-[#efeafd] px-3 py-2 text-left lg:sticky lg:left-0">
                Branch
              </th>
              <th className="w-[180px] min-w-[180px] bg-[#efeafd] px-3 py-2 text-left lg:sticky lg:left-[140px]">
                Category
              </th>
              <th className="w-[260px] min-w-[260px] bg-[#efeafd] px-3 py-2 text-left lg:sticky lg:left-[320px]">
                Product
              </th>
              {renderMetricHeads(cols)}
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {isError && (
              <tr>
                <td colSpan={totalCols} className="px-4 py-6 text-red-600">
                  {error?.data?.msg ||
                    error?.data?.message ||
                    "Failed to load report"}
                </td>
              </tr>
            )}

            {isFetching &&
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={`sk-${i}`} cols={totalCols} />
              ))}

            {!isFetching && !visibleRows.length && !isError && (
              <tr>
                <td
                  colSpan={totalCols}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No records found.
                </td>
              </tr>
            )}

            {!isFetching && (
              <AnimatePresence>
                {visibleRows.map((r, index) => {
                  const zebra = index % 2 === 0 ? "bg-white" : "bg-slate-50/60";
                  return (
                    <motion.tr
                      key={`${r?.branch?.id || "-"}-${r?.category?.id || "-"}-${
                        r?.product?.id || "-"
                      }-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.18, delay: index * 0.01 }}
                      className={`${zebra} border-t hover:bg-[#f7f4ff] transition`}
                    >
                      <td className="w-[140px] min-w-[140px] bg-inherit px-3 py-2 lg:sticky lg:left-0">
                        <span className="block truncate">
                          {r?.branch?.name || "-"}
                        </span>
                      </td>
                      <td className="w-[180px] min-w-[180px] bg-inherit px-3 py-2 lg:sticky lg:left-[140px]">
                        <span className="block truncate">
                          {r?.category?.name || "-"}
                        </span>
                      </td>
                      <td className="w-[260px] min-w-[260px] bg-inherit px-3 py-2 lg:sticky lg:left-[320px]">
                        <span className="block truncate">
                          {r?.product?.name || "-"}
                        </span>
                      </td>

                      {renderMetricCells(r, cols)}
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            )}
          </tbody>

          {/* GRAND footer */}
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-gradient-to-r from-[#f5f0ff] to-[#ede7ff] border-t shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                <td className="w-[140px] min-w-[140px] bg-gradient-to-r from-[#f5f0ff] to-[#ede7ff] px-3 py-3 font-semibold text-[#4b3d91] lg:sticky lg:left-0">
                  GRAND
                </td>
                <td className="w-[180px] min-w-[180px] bg-gradient-to-r from-[#f5f0ff] to-[#ede7ff] px-3 py-3 lg:sticky lg:left-[140px]" />
                <td className="w-[260px] min-w-[260px] bg-gradient-to-r from-[#f5f0ff] to-[#ede7ff] px-3 py-3 lg:sticky lg:left-[320px]" />

                {renderMetricCells(grand, cols, true)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Bottom metric badges */}
      {rows.length > 0 && (
        <div className="hidden">
          <div className="px-3 sm:px-4 py-2 flex flex-wrap gap-2 items-center">
            <Badge label={`Items: ${fmt0(toNum(grand?.items))}`} />
            <Badge label={`Qty: ${fmt0(toNum(grand?.qty))}`} />
            <Badge label={`Net: ${fmt3(toNum(grand?.netWt))} g`} />
            <Badge label={`Fine: ${fmt3(toNum(grand?.fineWt))} g`} />
            <Badge label={`Amount: ₹ ${fmt2(toNum(grand?.amount))}`} />
          </div>
        </div>
      )}

      {/* Footer pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-white px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
        <div className="hidden text-slate-600 sm:block">
          Total <span className="font-semibold">{total}</span> entries
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 20);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none">
            <button
              type="button"
              disabled={!canPrev || isFetching}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <FiChevronLeft />
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from(
                    { length: Math.max(1, totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {Math.max(1, totalPages)}</span>
              </span>
              <span className="hidden sm:inline">
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{Math.max(1, totalPages)}</span>
              </span>
            </span>

            <button
              type="button"
              disabled={!canNext || isFetching}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="hidden sm:inline">Next</span>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Modal (sales report style) */}
      <AnimatePresence>
        {isSummaryOpen && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/45 flex items-center justify-center p-3 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSummaryOpen(false)}
          >
            <motion.div
              className="w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden bg-white"
              initial={{ scale: 0.92, y: 18, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 18, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative px-4 sm:px-6 py-4 text-white bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce]">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/30 blur-2xl" />
                  <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-white/30 blur-2xl" />
                </div>

                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm sm:text-base font-semibold tracking-tight">
                      URD Summary (Grand Totals)
                    </div>
                    {humanRange ? (
                      <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95">
                        {humanRange}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95">
                        Current filters range
                      </div>
                    )}
                  </div>

                  <button
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition"
                    onClick={() => setIsSummaryOpen(false)}
                    title="Close"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              <div className="p-3 sm:p-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  <SummaryCard
                    label="Items"
                    value={fmt0(toNum(grand?.items))}
                  />
                  <SummaryCard label="Qty" value={fmt0(toNum(grand?.qty))} />
                  <SummaryCard
                    label="Net Wt"
                    value={`${fmt3(toNum(grand?.netWt))} g`}
                  />
                  <SummaryCard
                    label="Fine Wt"
                    value={`${fmt3(toNum(grand?.fineWt))} g`}
                  />
                  <SummaryCard
                    label="Amount"
                    value={`₹ ${fmt2(toNum(grand?.amount))}`}
                  />
                  <SummaryCard
                    label="Rows (page)"
                    value={fmt0(visibleRows.length)}
                  />
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] sm:text-[11px] text-slate-500">
                    Tip: ESC to close
                  </div>
                  <button
                    onClick={() => setIsSummaryOpen(false)}
                    className="px-3 sm:px-4 py-1.5 rounded-full bg-[#6b59c6] text-white text-[11px] font-semibold hover:opacity-90 transition"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter Modal */}
      <AnimatePresence>
        {false && isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/45 flex items-center justify-center p-3 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden bg-white"
              initial={{ scale: 0.92, y: 18, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 18, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 sm:px-6 py-4 text-white bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce] flex items-center justify-between">
                <div>
                  <div className="text-sm sm:text-base font-semibold">
                    Filters
                  </div>
                  <div className="text-[11px] text-white/85">
                    Date range • Branch • Category • Product
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition"
                    onClick={() => setIsFilterOpen(false)}
                    title="Close"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="mb-4 rounded-2xl border border-violet-100 bg-violet-50/50 p-3">
                  <div className="mb-2 text-xs font-semibold text-slate-700">
                    Report period
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="min-w-0 text-[11px] text-slate-500">
                      From
                      <input
                        type="date"
                        value={draftFrom}
                        onChange={(event) => setDraftFrom(event.target.value)}
                        className="mt-1 h-10 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-violet-300"
                      />
                    </label>
                    <label className="min-w-0 text-[11px] text-slate-500">
                      To
                      <input
                        type="date"
                        value={draftTo}
                        onChange={(event) => setDraftTo(event.target.value)}
                        className="mt-1 h-10 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-violet-300"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Branch</label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={branchOptions}
                      value={branchDraft}
                      onChange={onBranchDraft}
                      placeholder="Select branch…"
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-500">Category</label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={categoryOptions}
                      value={categoryDraft}
                      onChange={onCategoryDraft}
                      placeholder="Select category…"
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-500">Product</label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={productOptions}
                      value={productDraft}
                      onChange={onProductDraft}
                      placeholder="Select product…"
                      isDisabled={!draftFilters.categoryId}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="px-4 py-2 rounded-full border text-slate-700 hover:bg-slate-50 text-[12px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-5 py-2 rounded-full bg-[#6b59c6] text-white text-[12px] font-semibold hover:opacity-90"
                  >
                    Apply & Run
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- cells & heads renderers ---------- */
function renderMetricHeads(cols) {
  const heads = [];
  if (cols.items)
    heads.push(
      <th key="items" className="px-3 py-2 text-right">
        Items
      </th>,
    );
  if (cols.qty)
    heads.push(
      <th key="qty" className="px-3 py-2 text-right">
        Qty
      </th>,
    );
  if (cols.total)
    heads.push(
      <th key="total" className="px-3 py-2 text-right">
        Total
      </th>,
    );
  if (cols.packing)
    heads.push(
      <th key="packing" className="px-3 py-2 text-right">
        Packing
      </th>,
    );
  if (cols.gross)
    heads.push(
      <th key="gross" className="px-3 py-2 text-right">
        Gross
      </th>,
    );
  if (cols.stone)
    heads.push(
      <th key="stone" className="px-3 py-2 text-right">
        Stone
      </th>,
    );
  if (cols.diamond)
    heads.push(
      <th key="dia" className="px-3 py-2 text-right">
        Diamond (ct)
      </th>,
    );
  if (cols.net)
    heads.push(
      <th key="net" className="px-3 py-2 text-right">
        Net
      </th>,
    );
  if (cols.fine)
    heads.push(
      <th key="fine" className="px-3 py-2 text-right">
        Fine
      </th>,
    );
  if (cols.amount)
    heads.push(
      <th key="amt" className="px-3 py-2 text-right">
        Amount (₹)
      </th>,
    );
  return heads.map((head, index) =>
    React.cloneElement(head, {
      className: `${head.props.className} ${
        index === 0
          ? "border-l-2 border-l-violet-300 bg-violet-50/60"
          : "border-l border-l-slate-200/70"
      }`,
    }),
  );
}

function renderMetricCells(pack, cols, strong = false) {
  const val0 = (x) => (strong ? <b>{fmt0(x)}</b> : fmt0(x));
  const v3 = (x) => (strong ? <b>{fmt3(x)}</b> : fmt3(x));
  const v2 = (x) => (strong ? <b>{fmt2(x)}</b> : fmt2(x));

  const tds = [];
  if (cols.items)
    tds.push(
      <td key="items" className="px-3 py-2 text-right tabular-nums">
        {val0(toNum(pack?.items))}
      </td>,
    );
  if (cols.qty)
    tds.push(
      <td key="qty" className="px-3 py-2 text-right tabular-nums">
        {val0(toNum(pack?.qty))}
      </td>,
    );
  if (cols.total)
    tds.push(
      <td key="total" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.totalWt))}
      </td>,
    );
  if (cols.packing)
    tds.push(
      <td key="packing" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.packingWt))}
      </td>,
    );
  if (cols.gross)
    tds.push(
      <td key="gross" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.grossWt))}
      </td>,
    );
  if (cols.stone)
    tds.push(
      <td key="stone" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.stoneWt))}
      </td>,
    );
  if (cols.diamond)
    tds.push(
      <td key="diamond" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.diamondWt))}
      </td>,
    );
  if (cols.net)
    tds.push(
      <td key="net" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.netWt))}
      </td>,
    );
  if (cols.fine)
    tds.push(
      <td key="fine" className="px-3 py-2 text-right tabular-nums">
        {v3(toNum(pack?.fineWt))}
      </td>,
    );
  if (cols.amount)
    tds.push(
      <td key="amt" className="px-3 py-2 text-right tabular-nums">
        {v2(toNum(pack?.amount))}
      </td>,
    );
  return tds.map((cell, index) =>
    React.cloneElement(cell, {
      className: `${cell.props.className} ${
        index === 0
          ? "border-l-2 border-l-slate-300"
          : "border-l border-l-slate-200/70"
      }`,
    }),
  );
}

/* ---------- small components ---------- */
function ColumnPicker({ cols, setCols, dense, setDense }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const toggle = (k) => setCols((c) => ({ ...c, [k]: !c[k] }));

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = [
    ["items", "Items"],
    ["qty", "Qty"],
    ["total", "Total Wt"],
    ["packing", "Packing Wt"],
    ["gross", "Gross Wt"],
    ["stone", "Stone Wt"],
    ["diamond", "Diamond (ct)"],
    ["net", "Net Wt"],
    ["fine", "Fine Wt"],
    ["amount", "Amount (₹)"],
  ];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white/15 text-[11px] font-semibold shadow-sm transition hover:bg-white/20 sm:w-auto sm:rounded-full sm:px-3"
        title="Columns"
      >
        <FiSettings />
        <span className="hidden sm:inline">Columns</span>
        <span className="hidden sm:inline">
          {open ? <FiChevronUp /> : <FiChevronDown />}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            className="absolute right-0 mt-2 w-64 rounded-2xl border bg-white shadow-xl z-50 p-2 overflow-hidden"
          >
            <div className="px-2 py-2 text-xs font-semibold text-slate-700 border-b bg-slate-50">
              Columns visibility
            </div>

            <div className="p-2 space-y-1">
              {items.map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 px-2 py-1.5 text-[12px] hover:bg-slate-50 rounded-xl cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!cols[key]}
                    onChange={() => toggle(key)}
                  />
                  <span className="text-slate-700">{label}</span>
                </label>
              ))}

              <div className="h-px my-2 bg-gray-200" />

              <label className="flex items-center gap-2 px-2 py-1.5 text-[12px] hover:bg-slate-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={dense}
                  onChange={() => setDense((d) => !d)}
                />
                <span className="text-slate-700">Compact rows</span>
              </label>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-3 py-3 shadow-sm">
      <div className="text-[11px] font-semibold text-slate-600">{label}</div>
      <div className="mt-1 text-sm sm:text-base font-extrabold text-slate-900">
        {value}
      </div>
    </div>
  );
}

function Badge({ label }) {
  return (
    <span className="inline-flex items-center text-[11px] bg-violet-50 text-violet-700 border border-violet-200 px-2 py-1 rounded-full">
      {label}
    </span>
  );
}

function Chip({ label, onClear }) {
  return (
    <motion.span
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      className="inline-flex items-center gap-1 text-[11px] bg-white border border-violet-200 text-violet-700 px-2 py-1 rounded-full shadow-sm"
    >
      <span className="max-w-[260px] truncate">{label}</span>
      <button onClick={onClear} className="hover:text-violet-900">
        <FiX />
      </button>
    </motion.span>
  );
}
